// Murphy Door JS Error Tracker — deploy to Shopify theme as assets/js-error-tracker.js
// Add to theme.liquid: <script src="{{ 'js-error-tracker.js' | asset_url }}" defer></script>
//
// Captures error events + unhandledrejection events and batches them to the Super App.
// Batch cap: 10 errors per flush. Flush interval: 2 seconds (or on page unload).
// Session ID + user agent are attached only with confirmed analytics consent.
// Sent with fetch(keepalive, credentials:'omit'), never sendBeacon: a beacon
// always carries the .murphydoor.com cookie jar (_ga, _fbp, _gcl_au, Elevar's
// gclid...) to super-app.murphydoor.com, which leaks ad identifiers for
// visitors who declined. keepalive keeps errors captured at unload.
//
// This script is intentionally self-protecting: errors within the tracker are silently
// swallowed so the tracker itself never causes visible issues on the storefront.

(function () {
  'use strict';

  var ENDPOINT = 'https://super-app.murphydoor.com/api/storefront/js-errors';
  var SESSION_KEY = 'md_err_sid';
  var MAX_BATCH = 10;
  var FLUSH_INTERVAL_MS = 2000;
  var MAX_ERRORS_PER_PAGE = 50;
  // The Super App endpoint rejects bodies over 8 KB (readJsonCapped) without
  // an error the client can see, so every request stays under this size.
  var MAX_BODY_BYTES = 7000;

  // Identifiers ride along ONLY with confirmed analytics consent. The error
  // report itself (message, stack, path) is first-party diagnostics and is
  // sent regardless; the per-session id and user agent are what make it
  // linkable to a visitor, and a declined visitor must not be linkable.
  // Checked at FLUSH time, not load time: the consent API loads after this
  // script, and mdConsent's helpers fail OPEN before it answers, so
  // isAuthoritative() is required to tell a real "yes" from a guess.
  function analyticsConsented() {
    try {
      var c = window.mdConsent;
      return !!(c && c.isAuthoritative() && c.analyticsAllowed());
    } catch (e) {
      return false;
    }
  }

  // Session id for dedup (per browser session, not per device). Created
  // lazily so a non-consenting visitor never gets the sessionStorage write.
  var sid = null;
  function getSid() {
    if (sid) return sid;
    try {
      sid = sessionStorage.getItem(SESSION_KEY);
      if (!sid) {
        sid =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : Math.random().toString(36).slice(2) +
              Math.random().toString(36).slice(2);
        sessionStorage.setItem(SESSION_KEY, sid);
      }
    } catch (e) {
      sid = 'unknown';
    }
    return sid;
  }

  // Strip query strings and fragments from every URL in a string. Script URLs
  // and stack frames carry the page's ?gclid=/fbclid=/utm_* when a script was
  // loaded with them, and those are ad identifiers. A stack frame's trailing
  // :line:col is kept so the report stays debuggable.
  function scrubUrls(str) {
    if (!str) return str;
    return String(str).replace(
      /(https?:\/\/[^\s?#()'"]+)[?#][^\s()'"]*?(:\d+:\d+)?(?=[\s()'"]|$)/g,
      '$1$2'
    );
  }

  var buffer = [];
  var timer = null;
  var errorCount = 0;

  function flushNow() {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (!buffer.length) return;
    var batch = buffer.splice(0, MAX_BATCH);
    try {
      if (analyticsConsented()) {
        var id = getSid();
        for (var i = 0; i < batch.length; i++) {
          batch[i].sessionId = id;
          batch[i].userAgent = navigator.userAgent;
        }
      }
      // Pack entries into requests that each stay under MAX_BODY_BYTES; an
      // entry too big on its own has its stack trimmed to fit.
      var chunk = [];
      for (var j = 0; j < batch.length; j++) {
        var entry = fitEntry(batch[j]);
        if (chunk.length && bodyBytes(chunk.concat([entry])) > MAX_BODY_BYTES) {
          send(chunk);
          chunk = [];
        }
        chunk.push(entry);
      }
      if (chunk.length) send(chunk);
    } catch (e) {
      // Swallow all errors — error tracker must never throw
    }
  }

  function bodyBytes(entries) {
    var body = JSON.stringify({ errors: entries });
    try {
      return new TextEncoder().encode(body).length;
    } catch (e) {
      return body.length * 3;
    }
  }

  function fitEntry(entry) {
    while (entry.stack && bodyBytes([entry]) > MAX_BODY_BYTES) {
      entry.stack =
        entry.stack.length > 200
          ? entry.stack.slice(0, Math.floor(entry.stack.length / 2))
          : null;
    }
    return entry;
  }

  function send(entries) {
    // text/plain keeps this a CORS "simple" request (no preflight), and
    // no-cors means the opaque response never logs a CORS error. The
    // server parses JSON from the text/plain body.
    // credentials:'omit' is the point: no cookies ride along, whatever the
    // visitor's consent. There is deliberately NO sendBeacon fallback,
    // because sendBeacon cannot omit cookies.
    // Feedback loop: third-party fetch wrappers (FigPii, etc.) can turn a
    // failed POST into an unhandled rejection. The .catch swallows ours, and
    // capture() drops anything whose stack names this file.
    if (typeof fetch !== 'function') return;
    fetch(ENDPOINT, {
      method: 'POST',
      mode: 'no-cors',
      credentials: 'omit',
      keepalive: true,
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({ errors: entries }),
    }).catch(function () {});
  }

  // Drop a stack overflow raised by the Google iOS apps' own injected script.
  // Chrome for iOS (CriOS) and the Google app (GSA) inject a Closure-compiled
  // script into every page they open. It has no src, so WebKit reports its
  // frames against the PAGE URL, and a mutual recursion in it (two minified
  // names, e.g. Lk/Jk, renamed with each app release) throws "Maximum call
  // stack size exceeded" on every kind of page. Production data from
  // 2026-04 to 2026-10: 160 reports on 42+ pages, all from CriOS or GSA, none from
  // Safari or any other browser. The frames' line:col positions do not exist
  // in the served HTML. ClickUp 868mfqgd6 / fingerprint a9d8c74c.
  // The match is narrow on purpose. It needs the UA, the message, and a
  // stack where no frame comes from a .js file, so a real overflow in a theme
  // asset is still reported, and so is any error from any other browser.
  var GOOGLE_IOS_UA = /\b(CriOS|GSA)\//;
  function isGoogleIosInjectedOverflow(msg, stack) {
    if (!GOOGLE_IOS_UA.test(navigator.userAgent || '')) return false;
    if (String(msg).indexOf('Maximum call stack size exceeded') === -1) {
      return false;
    }
    // An empty stack and frames pointing at a .js asset both stay reported.
    if (!stack || /\.js(\?|:|\s|$)/.test(stack)) return false;
    return true;
  }

  /**
   * True when the error was thrown inside a script injected over CDP by
   * Puppeteer (chrome-devtools-mcp `evaluate_script` names its frames
   * `pptr:evaluate...`). No shopper's browser produces such a frame: it is a
   * UAT agent dispatching synthetic events. 2026-10-09: 20 hits of
   * "reading 'x'" in md-three-bundle.js (fingerprint 2af37222) were OrbitControls
   * handling synthetic touch pointermoves a UAT agent dispatched on the 3D PDP,
   * and they paged as a customer error. Tagged (`true` = "unknown_internal"
   * server side, the only free-form value its schema accepts), never dropped.
   */
  function isAutomationStack(stack) {
    return stack.indexOf('pptr:') !== -1;
  }

  function capture(msg, src, err, type) {
    // Hard cap: stop capturing after MAX_ERRORS_PER_PAGE to prevent runaway volume
    if (errorCount >= MAX_ERRORS_PER_PAGE) return;

    // Prevent feedback loop: third-party fetch wrappers (e.g. FigPii) can create
    // unhandled rejections from our own POST failures, which the tracker would then
    // re-capture endlessly. Break the cycle by ignoring errors from our own calls.
    var stack = err && err.stack ? String(err.stack) : '';
    if (stack.indexOf('js-error-tracker') !== -1) return;

    if (isGoogleIosInjectedOverflow(msg, stack)) return;

    errorCount++;

    var entry = {
      message: scrubUrls(String(msg)).slice(0, 500),
      filename: src ? scrubUrls(src) : null,
      stack:
        err && err.stack ? scrubUrls(String(err.stack)).slice(0, 2000) : null,
      pageUrl: location.origin + location.pathname,
      severity: type,
      // Staff browsers (snippets/md-internal-traffic.liquid) are TAGGED, not
      // suppressed: their errors are real bugs, but they must be separable
      // from customer error rates. 'staff_cookie' | 'render_job' | false.
      internal:
        window.MD_INTERNAL_TRAFFIC === true
          ? window.MD_INTERNAL_TRAFFIC_REASON || true
          : isAutomationStack(stack),
    };

    buffer.push(entry);

    if (buffer.length >= MAX_BATCH) {
      // Hard cap reached — flush immediately, don't wait for timer
      flushNow();
    } else if (!timer) {
      timer = setTimeout(flushNow, FLUSH_INTERVAL_MS);
    }
  }

  // Capture synchronous JS errors via addEventListener (not window.onerror assignment).
  // addEventListener is additive — other scripts (e.g. Bugsnag) that assign window.onerror
  // directly will overwrite an assignment, but cannot remove our event listener.
  window.addEventListener('error', function (e) {
    try {
      capture(e.message, e.filename, e.error, 'error');
    } catch (ex) {
      // Swallow — tracker must never throw
    }
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', function (e) {
    try {
      var reason = e.reason;
      var msg = reason instanceof Error ? reason.message : String(reason);
      var err = reason instanceof Error ? reason : null;
      capture(msg, null, err, 'unhandledrejection');
    } catch (ex) {
      // Swallow — tracker must never throw
    }
  });

  // Flush remaining errors on page hide (pagehide not beforeunload — preserves bfcache)
  window.addEventListener('pagehide', function () {
    try {
      flushNow();
    } catch (e) {
      // Swallow
    }
  });
})();
